import { useEffect, useState, useContext } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Tag,
  Button,
  Space,
  Spin,
  Divider,
  Modal,
  Image,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  notification,
  Dropdown,
  Popconfirm,
} from "antd";
import {
  HomeOutlined,
  UserOutlined,
  CalendarOutlined,
  TeamOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EllipsisOutlined,
  AppstoreOutlined,
  DollarCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../context/AppContext";
import "./Styles.css";

const { Title, Text, Paragraph } = Typography;

// Zambian theme colors
const ZAMBIA_THEME = {
  primary: "#078930",
  secondary: "#EF3340",
  accent: "#FFB81C",
  neutral: "#2D2D2D",
};

const BoardingHouses = () => {
  const token = localStorage.getItem("token");
  const [boardingHouses, setBoardingHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // CRUD Modal States
  const [houseModalVisible, setHouseModalVisible] = useState(false);
  const [roomModalVisible, setRoomModalVisible] = useState(false);
  const [bedSpaceModalVisible, setBedSpaceModalVisible] = useState(false);
  const [rentalPaymentModalVisible, setRentalPaymentModalVisible] = useState(false);
  const [editingHouse, setEditingHouse] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editingBedSpace, setEditingBedSpace] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBedSpace, setSelectedBedSpace] = useState(null);
  const [accounts, setAccounts] = useState([]);

  // Forms
  const [houseForm] = Form.useForm();
  const [roomForm] = Form.useForm();
  const [bedSpaceForm] = Form.useForm();
  const [rentalPaymentForm] = Form.useForm();

  const { company } = useContext(AppContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBoardingHouses();
    fetchAccounts();
  }, [company?.id, token]);

  const fetchBoardingHouses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/boarding_houses?fields=*,rooms.*,rooms.bed_spaces.*&filter[company]=${company.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setBoardingHouses(response.data.data);
    } catch (err) {
      console.error("Error fetching boarding houses:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/accounts?filter[company]=${company.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setAccounts(response.data.data);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    }
  };

  const getSummaryStats = () => {
    const totalHouses = boardingHouses.length;
    const totalBedSpaces = boardingHouses.reduce(
      (sum, house) =>
        sum +
        house.rooms.reduce(
          (roomSum, room) => roomSum + room.bed_spaces.length,
          0
        ),
      0
    );
    const availableBedSpaces = boardingHouses.reduce(
      (sum, house) =>
        sum +
        house.rooms.reduce(
          (roomSum, room) =>
            roomSum +
            room.bed_spaces.filter((bed) => bed.status === "Available").length,
          0
        ),
      0
    );
    const totalRentalValue = boardingHouses.reduce(
      (sum, house) =>
        sum +
        house.rooms.reduce(
          (roomSum, room) =>
            roomSum +
            room.bed_spaces.reduce(
              (bedSum, bed) => bedSum + (bed.rental_amount || 0),
              0
            ),
          0
        ),
      0
    );

    return (
      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Boarding Houses"
              value={totalHouses}
              valueStyle={{ color: ZAMBIA_THEME.primary }}
              prefix={<HomeOutlined />}
            />
          </Card>
        </Col>
        {/* <Col span={5}>
          <Card>
            <Statistic
              title="Total Rooms"
              value={totalRooms}
              valueStyle={{ color: ZAMBIA_THEME.accent }}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col> */}
        <Col span={6}>
          <Card>
            <Statistic
              title="Bed Spaces"
              value={totalBedSpaces}
              valueStyle={{ color: "#1890ff" }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Bed Spaces Available"
              value={availableBedSpaces}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Rent"
              value={totalRentalValue}
              prefix="ZMW"
              valueStyle={{ color: ZAMBIA_THEME.secondary }}
              formatter={(value) => value.toLocaleString()}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const showHouseDetails = (house) => {
    setSelectedHouse(house);
    setDetailModalVisible(true);
  };

  // BOARDING HOUSE CRUD FUNCTIONS
  const handleCreateHouse = () => {
    houseForm.resetFields();
    setEditingHouse(null);
    setHouseModalVisible(true);
  };

  const handleEditHouse = (house) => {
    houseForm.setFieldsValue({
      address: house.address,
      caretaker: house.caretaker,
      thumbnail: house.thumbnail,
      house_rules: house.house_rules,
      amenities: house.amenities,
    });
    setEditingHouse(house);
    setHouseModalVisible(true);
  };

  const handleDeleteHouse = async (houseId) => {
    try {
      await axios.delete(
        `https://zedlenders.pickmesms.com/items/boarding_houses/${houseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notification.success({ message: "Boarding house deleted successfully!" });
      fetchBoardingHouses();
    } catch (err) {
      notification.error({ message: "Failed to delete boarding house" });
    }
  };

  const handleHouseSubmit = async (values) => {
    try {
      const payload = { ...values, company: company.id, status: "active" };
      const url = editingHouse
        ? `https://zedlenders.pickmesms.com/items/boarding_houses/${editingHouse.id}`
        : `https://zedlenders.pickmesms.com/items/boarding_houses`;

      const method = editingHouse ? "patch" : "post";
      await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notification.success({
        message: `Boarding house ${
          editingHouse ? "updated" : "created"
        } successfully!`,
      });
      setHouseModalVisible(false);
      fetchBoardingHouses();
    } catch (err) {
      notification.error({ message: "Failed to save boarding house" });
    }
  };

  // ROOM CRUD FUNCTIONS
  const handleCreateRoom = (house) => {
    roomForm.resetFields();
    setEditingRoom(null);
    setSelectedHouse(house);
    setRoomModalVisible(true);
  };

  const handleEditRoom = (room, house) => {
    roomForm.setFieldsValue({
      room_number: room.room_number,
      room_type: room.room_type,
      features: room.features,
    });
    setEditingRoom(room);
    setSelectedHouse(house);
    setRoomModalVisible(true);
  };

  const handleDeleteRoom = async (roomId) => {
    try {
      await axios.delete(
        `https://zedlenders.pickmesms.com/items/rooms/${roomId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notification.success({ message: "Room deleted successfully!" });
      fetchBoardingHouses();
    } catch (err) {
      notification.error({ message: "Failed to delete room" });
    }
  };

  const handleRoomSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        boarding_house_id: selectedHouse.id,
        status: "published",
      };

      const url = editingRoom
        ? `https://zedlenders.pickmesms.com/items/rooms/${editingRoom.id}`
        : `https://zedlenders.pickmesms.com/items/rooms`;

      const method = editingRoom ? "patch" : "post";
      await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notification.success({
        message: `Room ${editingRoom ? "updated" : "created"} successfully!`,
      });
      setRoomModalVisible(false);
      fetchBoardingHouses();
    } catch (err) {
      notification.error({ message: "Failed to save room" });
    }
  };

  // BED SPACE CRUD FUNCTIONS
  const handleCreateBedSpace = (room, house) => {
    bedSpaceForm.resetFields();
    setEditingBedSpace(null);
    setSelectedRoom(room);
    setSelectedHouse(house);
    setBedSpaceModalVisible(true);
  };

  const handleEditBedSpace = (bedSpace, room, house) => {
    bedSpaceForm.setFieldsValue({
      bed_label: bedSpace.bed_label,
      rental_amount: bedSpace.rental_amount,
      rental_due_date: bedSpace.rental_due_date
        ? moment(bedSpace.rental_due_date)
        : null,
      next_payment_date: bedSpace.next_payment_date
        ? moment(bedSpace.next_payment_date)
        : null,
      rent_overdue_amount: bedSpace.rent_overdue_amount || 0,
      status: bedSpace.status,
    });
    setEditingBedSpace(bedSpace);
    setSelectedRoom(room);
    setSelectedHouse(house);
    setBedSpaceModalVisible(true);
  };

  const handleDeleteBedSpace = async (bedSpaceId) => {
    try {
      await axios.delete(
        `https://zedlenders.pickmesms.com/items/bed_spaces/${bedSpaceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notification.success({ message: "Bed space deleted successfully!" });
      fetchBoardingHouses();
    } catch (err) {
      notification.error({ message: "Failed to delete bed space" });
    }
  };

  const handleBedSpaceSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        room_id: selectedRoom.id,
        rental_due_date: values.rental_due_date?.format("YYYY-MM-DD"),
        next_payment_date: values.next_payment_date?.format("YYYY-MM-DD"),
        rent_overdue_amount: values.rent_overdue_amount || 0,
      };

      const url = editingBedSpace
        ? `https://zedlenders.pickmesms.com/items/bed_spaces/${editingBedSpace.id}`
        : `https://zedlenders.pickmesms.com/items/bed_spaces`;

      const method = editingBedSpace ? "patch" : "post";
      await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notification.success({
        message: `Bed space ${
          editingBedSpace ? "updated" : "created"
        } successfully!`,
      });
      setBedSpaceModalVisible(false);
      fetchBoardingHouses();
    } catch (err) {
      notification.error({ message: "Failed to save bed space" });
    }
  };

  // RENTAL PAYMENT FUNCTIONS
  const handleCollectRental = (bedSpace, room, house) => {
    rentalPaymentForm.resetFields();
    rentalPaymentForm.setFieldsValue({
      bed_space_id: bedSpace.id,
      rental_amount: bedSpace.rental_amount,
      transaction_date: moment(),
    });
    setSelectedBedSpace(bedSpace);
    setSelectedRoom(room);
    setSelectedHouse(house);
    setRentalPaymentModalVisible(true);
  };

  const handleRentalPaymentSubmit = async (values) => {
    try {
      const paymentAmount = values.amount_paid;
      const overdueAmount = selectedBedSpace.rent_overdue_amount || 0;
      
      // Calculate how much goes to overdue vs current rent
      let overduePayment = 0;
      
      if (overdueAmount > 0) {
        overduePayment = Math.min(paymentAmount, overdueAmount);
      }

      // Create transaction record
      const transactionPayload = {
        transaction_type: "rentals",
        amount: paymentAmount,
        transaction_date: values.transaction_date.format('YYYY-MM-DD'),
        account_id: values.account_id,
        payment_method: values.payment_method,
        reference: values.reference || `Rental payment for ${selectedBedSpace.bed_label}`,
        notes: values.notes,
        bed_space_id: selectedBedSpace.id,
        company: company.id,
        status: "completed",
        transaction_status: "Completed"
      };

      await axios.post(
        `https://zedlenders.pickmesms.com/items/transactions`,
        transactionPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update bed space with new overdue amount and next payment date
      const newOverdueAmount = values.rent_overdue_amount || 0; // Manual override
      const nextPaymentDate = values.next_payment_date?.format('YYYY-MM-DD') || moment(selectedBedSpace.next_payment_date || selectedBedSpace.rental_due_date).add(1, 'month').format('YYYY-MM-DD');
      
      const bedSpaceUpdatePayload = {
        rent_overdue_amount: newOverdueAmount,
        next_payment_date: nextPaymentDate
      };

      await axios.patch(
        `https://zedlenders.pickmesms.com/items/bed_spaces/${selectedBedSpace.id}`,
        bedSpaceUpdatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update account balance
      const account = accounts.find(acc => acc.id === values.account_id);
      if (account) {
        await axios.patch(
          `https://zedlenders.pickmesms.com/items/accounts/${values.account_id}`,
          { balance: account.balance + paymentAmount },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      notification.success({
        message: "Rental payment recorded successfully!",
        description: `Payment of ZMW ${paymentAmount} recorded. Redirecting to transactions page...`
      });

      setRentalPaymentModalVisible(false);
      
      // Navigate to transactions page to show the recorded payment
      setTimeout(() => {
        navigate('/transactions');
      }, 1500);
    } catch (err) {
      notification.error({ 
        message: "Failed to record rental payment",
        description: err.response?.data?.errors?.[0]?.message || "Please try again"
      });
    }
  };

  const getRoomOccupancyStatus = (room) => {
    const totalBeds = room.bed_spaces.length;
    const availableBeds = room.bed_spaces.filter(
      (bed) => bed.status === "Available"
    ).length;

    if (availableBeds === totalBeds) {
      return { status: "Available", color: "#52c41a" };
    } else if (availableBeds === 0) {
      return { status: "Full", color: "#ff4d4f" };
    } else {
      return { status: "Partial", color: "#faad14" };
    }
  };

  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "20%" }}
      >
        <Spin size="large" tip="Loading boarding houses..." />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 24px" }}>
      {/* Header */}
      <Card
        style={{
          background: `linear-gradient(135deg, ${ZAMBIA_THEME.primary}40, ${ZAMBIA_THEME.accent}30)`,
          marginBottom: 24,
          borderRadius: 12,
          border: `1px solid ${ZAMBIA_THEME.primary}20`,
        }}
      >
        <Row align="middle" justify="space-between" gutter={[24, 0]}>
          <Col>
            <Space>
              <HomeOutlined
                style={{ fontSize: 48, color: ZAMBIA_THEME.primary }}
              />
              <div>
                <Title
                  level={2}
                  style={{ margin: 0, color: ZAMBIA_THEME.neutral }}
                >
                  Boarding House Management
                </Title>
                <Text style={{ fontSize: 16 }}>
                  Manage accommodations and track occupancy
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={handleCreateHouse}
              style={{
                background: ZAMBIA_THEME.primary,
                borderColor: ZAMBIA_THEME.primary,
                borderRadius: 8,
                padding: "0 24px",
                height: 48,
              }}
            >
              Add New Boarding House
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Summary Stats */}
      {getSummaryStats()}

      {/* Boarding Houses Grid */}
      <Row gutter={[24, 24]}>
        {boardingHouses.map((house) => {
          const totalBedSpaces = house.rooms.reduce(
            (sum, room) => sum + room.bed_spaces.length,
            0
          );
          const availableBedSpaces = house.rooms.reduce(
            (sum, room) =>
              sum +
              room.bed_spaces.filter((bed) => bed.status === "Available")
                .length,
            0
          );
          const occupancyRate =
            totalBedSpaces > 0
              ? (
                  ((totalBedSpaces - availableBedSpaces) / totalBedSpaces) *
                  100
                ).toFixed(1)
              : 0;
          const houseRentalAmount = house.rooms.reduce(
            (sum, room) =>
              sum +
              room.bed_spaces.reduce(
                (bedSum, bed) => bedSum + (bed.rental_amount || 0),
                0
              ),
            0
          );

          return (
            <Col xs={24} sm={12} lg={8} key={house.id}>
              <Card
                hoverable
                style={{
                  borderRadius: 16,
                  overflow: "hidden",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  border: `1px solid ${ZAMBIA_THEME.primary}20`,
                  height: "100%",
                }}
                cover={
                  <div style={{ 
                    position: "relative", 
                    height: 240, 
                    overflow: "hidden",
                    borderRadius: "16px 16px 0 0",
                    background: "#f5f5f5"
                  }}>
                    <Image
                      alt={`Boarding House ${house.address}`}
                      src={house.thumbnail || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSHZqj-XReJ2R76nji51cZl4ETk6-eHRmZBRw&s"}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        display: "block",
                        borderRadius: "16px 16px 0 0"
                      }}
                      preview={false}
                      fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjI0MCIgdmlld0JveD0iMCAwIDQwMCAyNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMjQwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMDAgMTIwTDE3MCA5MEwyMzAgOTBMMjAwIDEyMFoiIGZpbGw9IiNEOUQ5RDkiLz4KPHN2ZyBlbmQ+"
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        background: "rgba(0,0,0,0.7)",
                        borderRadius: 8,
                        padding: "4px 8px",
                      }}
                    >
                      <Text
                        style={{
                          color: "white",
                          fontSize: 12,
                          fontWeight: "bold",
                        }}
                      >
                        {occupancyRate}% Occupied
                      </Text>
                    </div>
                  </div>
                }
                actions={[
                  <Button
                    key="view"
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => showHouseDetails(house)}
                    style={{
                      background: ZAMBIA_THEME.primary,
                      borderColor: ZAMBIA_THEME.primary,
                      borderRadius: 8,
                      marginRight: 8,
                      marginLeft: 8,
                    }}
                  >
                    View Details
                  </Button>,
                  <Button
                    key="addRoom"
                    icon={<TeamOutlined />}
                    onClick={() => handleCreateRoom(house)}
                    style={{ borderRadius: 8, marginLeft: 24 }}
                  >
                    Add Room
                  </Button>,
                  <Dropdown
                    key="dropdown"
                    menu={{
                      items: [
                        {
                          key: "edit",
                          icon: <EditOutlined />,
                          label: "Edit Boarding House",
                          onClick: () => handleEditHouse(house),
                        },
                        {
                          key: "delete",
                          icon: <DeleteOutlined />,
                          danger: true,
                          label: (
                            <Popconfirm
                              title="Delete this boarding house?"
                              description="This will also delete all rooms and bed spaces."
                              onConfirm={() => handleDeleteHouse(house.id)}
                              okText="Yes"
                              cancelText="No"
                            >
                              Delete House
                            </Popconfirm>
                          ),
                        },
                      ],
                    }}
                    trigger={["click"]}
                  >
                    <Button
                      icon={<EllipsisOutlined />}
                      style={{ borderRadius: 8 }}
                    />
                  </Dropdown>,
                ]}
              >
                <div style={{ padding: "8px 0" }}>
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size={12}
                  >
                    <div>
                      <Space>
                        <EnvironmentOutlined
                          style={{ color: ZAMBIA_THEME.primary }}
                        />
                        <Title level={5} style={{ margin: 0 }}>
                          {house.address}
                        </Title>
                      </Space>
                    </div>

                    <div>
                      <Space>
                        <UserOutlined style={{ color: ZAMBIA_THEME.accent }} />
                        <Text strong>Caretaker:</Text>
                        <Text>{house.caretaker}</Text>
                      </Space>
                    </div>

                    <Row gutter={8}>
                      <Col span={12}>
                        <div
                          style={{
                            textAlign: "center",
                            padding: 8,
                            background: "#f0f0f0",
                            borderRadius: 8,
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Available Beds
                          </Text>
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: "bold",
                              color: "#52c41a",
                            }}
                          >
                            {availableBedSpaces}/{totalBedSpaces}
                          </div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div
                          style={{
                            textAlign: "center",
                            padding: 8,
                            background: "#f0f0f0",
                            borderRadius: 8,
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Total Rent
                          </Text>
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: "bold",
                              color: ZAMBIA_THEME.secondary,
                            }}
                          >
                            ZMW {houseRentalAmount?.toLocaleString()}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Room Types:
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        {Array.from(
                          new Set(house.rooms.map((room) => room.room_type))
                        ).map((type) => (
                          <Tag
                            key={type}
                            color={ZAMBIA_THEME.primary}
                            style={{ margin: "2px 4px 2px 0" }}
                          >
                            {type}
                          </Tag>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Space>
                        <CalendarOutlined
                          style={{ color: ZAMBIA_THEME.accent }}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Earliest Due:{" "}
                          {house.rooms.length > 0 &&
                          house.rooms[0].bed_spaces.length > 0
                            ? new Date(
                                house.rooms[0].bed_spaces[0].rental_due_date
                              ).toLocaleDateString()
                            : "N/A"}
                        </Text>
                      </Space>
                    </div>
                  </Space>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Detail Modal */}
      <Modal
        title={
          <Space>
            <HomeOutlined style={{ color: ZAMBIA_THEME.primary }} />
            <span>Boarding House Details - {selectedHouse?.address}</span>
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedHouse && (
          <div>
            <Row gutter={24}>
              <Col span={12}>
                <Image
                  src={selectedHouse.thumbnail || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSHZqj-XReJ2R76nji51cZl4ETk6-eHRmZBRw&s"}
                  alt="Boarding House"
                  style={{ width: "100%", borderRadius: 8 }}
                />
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: "100%" }} size={16}>
                  <div>
                    <Text strong>Caretaker:</Text>
                    <div>{selectedHouse.caretaker}</div>
                  </div>
                  <div>
                    <Text strong>Total Rental:</Text>
                    <div
                      style={{
                        color: ZAMBIA_THEME.secondary,
                        fontSize: 18,
                        fontWeight: "bold",
                      }}
                    >
                      ZMW{" "}
                      {selectedHouse.rooms
                        .reduce(
                          (sum, room) =>
                            sum +
                            room.bed_spaces.reduce(
                              (bedSum, bed) =>
                                bedSum + (bed.rental_amount || 0),
                              0
                            ),
                          0
                        )
                        .toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <Text strong>Status:</Text>
                    <Tag
                      color={
                        selectedHouse.status === "active" ? "green" : "red"
                      }
                    >
                      {selectedHouse.status.toUpperCase()}
                    </Tag>
                  </div>
                </Space>
              </Col>
            </Row>

            <Divider />

            <div>
              <Title level={5}>House Rules</Title>
              <Paragraph style={{ whiteSpace: "pre-line" }}>
                {selectedHouse.house_rules}
              </Paragraph>
            </div>

            <div>
              <Title level={5}>Amenities</Title>
              <Paragraph>{selectedHouse.amenities}</Paragraph>
            </div>

            <Divider />

            <div>
              <Row
                justify="space-between"
                align="middle"
                style={{ marginBottom: 16 }}
              >
                <Col>
                  <Title level={5}>Rooms ({selectedHouse.rooms.length})</Title>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleCreateRoom(selectedHouse)}
                    style={{
                      background: ZAMBIA_THEME.primary,
                      borderColor: ZAMBIA_THEME.primary,
                    }}
                  >
                    Add Room
                  </Button>
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                {selectedHouse.rooms.map((room) => (
                  <Col span={12} key={room.id}>
                    <Card size="small" style={{ borderRadius: 8 }}>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Text strong>Room {room.room_number}</Text>
                          <Space>
                            {(() => {
                              const occupancyInfo =
                                getRoomOccupancyStatus(room);
                              return (
                                <Tag color={occupancyInfo.color}>
                                  {occupancyInfo.status}
                                </Tag>
                              );
                            })()}
                            <Dropdown
                              menu={{
                                items: [
                                  {
                                    key: "addBed",
                                    icon: <AppstoreOutlined />,
                                    label: "Add Bed Space",
                                    onClick: () =>
                                      handleCreateBedSpace(room, selectedHouse),
                                  },
                                  {
                                    key: "edit",
                                    icon: <EditOutlined />,
                                    label: "Edit Room",
                                    onClick: () =>
                                      handleEditRoom(room, selectedHouse),
                                  },
                                  {
                                    key: "delete",
                                    icon: <DeleteOutlined />,
                                    danger: true,
                                    label: (
                                      <Popconfirm
                                        title="Delete this room?"
                                        description="This will also delete all bed spaces in this room."
                                        onConfirm={() =>
                                          handleDeleteRoom(room.id)
                                        }
                                        okText="Yes"
                                        cancelText="No"
                                      >
                                        Delete Room
                                      </Popconfirm>
                                    ),
                                  },
                                ],
                              }}
                              trigger={["click"]}
                            >
                              <Button
                                size="small"
                                icon={<EllipsisOutlined />}
                                style={{ border: "none" }}
                              />
                            </Dropdown>
                          </Space>
                        </div>
                        <div>
                          <Text type="secondary">Type: </Text>
                          <Text>{room.room_type}</Text>
                        </div>
                        <div>
                          <Text type="secondary">Features: </Text>
                          <Text>{room.features}</Text>
                        </div>
                        <div>
                          <Row justify="space-between" align="middle">
                            <Col>
                              <Text type="secondary">Bed Spaces: </Text>
                              <Text>{room.bed_spaces.length}</Text>
                            </Col>
                            <Col>
                              <Space>
                                <Button
                                  size="small"
                                  type="link"
                                  icon={<PlusOutlined />}
                                  onClick={() =>
                                    handleCreateBedSpace(room, selectedHouse)
                                  }
                                  style={{ padding: 0 }}
                                >
                                  Add Bed
                                </Button>
                              </Space>
                            </Col>
                          </Row>
                          <div style={{ marginTop: 8 }}>
                            {room.bed_spaces.map((bed) => (
                              <div key={bed.id} style={{ 
                                display: "inline-block", 
                                margin: "4px", 
                                border: "1px solid #f0f0f0", 
                                borderRadius: 8, 
                                padding: "8px",
                                background: "#fafafa"
                              }}>
                                <div style={{ marginBottom: 8 }}>
                                  <Tag
                                    color={
                                      bed.rent_overdue_amount > 0
                                        ? "red"
                                        : bed.status === "Available"
                                        ? "green"
                                        : "orange"
                                    }
                                    style={{
                                      margin: "0 4px 4px 0",
                                      userSelect: "none",
                                    }}
                                    title={`Monthly: ZMW ${
                                      bed.rental_amount || 0
                                    }${bed.rent_overdue_amount > 0 ? ` | Overdue: ZMW ${bed.rent_overdue_amount}` : ""} | Next Due: ${
                                      bed.next_payment_date || bed.rental_due_date
                                        ? new Date(
                                            bed.next_payment_date || bed.rental_due_date
                                          ).toLocaleDateString()
                                        : "N/A"
                                    }`}
                                  >
                                    {bed.bed_label} - ZMW {bed.rental_amount || 0}
                                    {bed.rent_overdue_amount > 0 && (
                                      <span style={{ color: "white", fontWeight: "bold" }}>
                                        {" "}⚠
                                      </span>
                                    )}
                                  </Tag>
                                  {bed.rent_overdue_amount > 0 && (
                                    <Tag color="volcano" size="small">
                                      Overdue: ZMW {bed.rent_overdue_amount}
                                    </Tag>
                                  )}
                                </div>
                                <Space size="small">
                                  <Button
                                    type="primary"
                                    size="small"
                                    icon={<DollarCircleOutlined />}
                                    onClick={() =>
                                      handleCollectRental(
                                        bed,
                                        room,
                                        selectedHouse
                                      )
                                    }
                                    style={{
                                      background: bed.rent_overdue_amount > 0 ? "#ff4d4f" : ZAMBIA_THEME.primary,
                                      borderColor: bed.rent_overdue_amount > 0 ? "#ff4d4f" : ZAMBIA_THEME.primary,
                                    }}
                                  >
                                    {bed.rent_overdue_amount > 0 ? "Pay Overdue" : "Collect"}
                                  </Button>
                                  <Dropdown
                                    menu={{
                                      items: [
                                        {
                                          key: "edit",
                                          icon: <EditOutlined />,
                                          label: "Edit Bed Space",
                                          onClick: () =>
                                            handleEditBedSpace(
                                              bed,
                                              room,
                                              selectedHouse
                                            ),
                                        },
                                        {
                                          key: "delete",
                                          icon: <DeleteOutlined />,
                                          danger: true,
                                          label: (
                                            <Popconfirm
                                              title="Delete this bed space?"
                                              onConfirm={() =>
                                                handleDeleteBedSpace(bed.id)
                                              }
                                              okText="Yes"
                                              cancelText="No"
                                            >
                                              Delete Bed Space
                                            </Popconfirm>
                                          ),
                                        },
                                      ],
                                    }}
                                    trigger={["click"]}
                                  >
                                    <Button size="small" icon={<EllipsisOutlined />} />
                                  </Dropdown>
                                </Space>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </div>
        )}
      </Modal>

      {/* Boarding House Modal */}
      <Modal
        title={
          <Space>
            <HomeOutlined style={{ color: ZAMBIA_THEME.primary }} />
            {editingHouse ? "Edit Boarding House" : "Add New Boarding House"}
          </Space>
        }
        open={houseModalVisible}
        onCancel={() => setHouseModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={houseForm} layout="vertical" onFinish={handleHouseSubmit}>
          <Form.Item
            label="Address"
            name="address"
            rules={[{ required: true, message: "Please enter address" }]}
          >
            <Input placeholder="Enter boarding house address" />
          </Form.Item>

          <Form.Item
            label="Caretaker"
            name="caretaker"
            rules={[{ required: true, message: "Please enter caretaker name" }]}
          >
            <Input placeholder="Enter caretaker name" />
          </Form.Item>

          <Form.Item label="Thumbnail URL" name="thumbnail">
            <Input placeholder="Enter image URL" />
          </Form.Item>

          <Form.Item label="House Rules" name="house_rules">
            <Input.TextArea rows={4} placeholder="Enter house rules..." />
          </Form.Item>

          <Form.Item label="Amenities" name="amenities">
            <Input.TextArea rows={3} placeholder="List amenities..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingHouse ? "Update" : "Create"} Boarding House
              </Button>
              <Button onClick={() => setHouseModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Room Modal */}
      <Modal
        title={
          <Space>
            <TeamOutlined style={{ color: ZAMBIA_THEME.primary }} />
            {editingRoom ? "Edit Room" : "Add New Room"}
          </Space>
        }
        open={roomModalVisible}
        onCancel={() => setRoomModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form form={roomForm} layout="vertical" onFinish={handleRoomSubmit}>
          <Form.Item
            label="Room Number"
            name="room_number"
            rules={[{ required: true, message: "Please enter room number" }]}
          >
            <Input placeholder="e.g., 101, A1, etc." />
          </Form.Item>

          <Form.Item
            label="Room Type"
            name="room_type"
            rules={[{ required: true, message: "Please select room type" }]}
          >
            <Select placeholder="Select room type">
              <Select.Option value="Single">Single</Select.Option>
              <Select.Option value="Double">Double</Select.Option>
              <Select.Option value="Triple">Triple</Select.Option>
              <Select.Option value="Dormitory">Dormitory</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Features" name="features">
            <Input.TextArea rows={3} placeholder="Describe room features..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRoom ? "Update" : "Create"} Room
              </Button>
              <Button onClick={() => setRoomModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Bed Space Modal */}
      <Modal
        title={
          <Space>
            <AppstoreOutlined style={{ color: ZAMBIA_THEME.primary }} />
            {editingBedSpace ? "Edit Bed Space" : "Add New Bed Space"}
          </Space>
        }
        open={bedSpaceModalVisible}
        onCancel={() => setBedSpaceModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={bedSpaceForm}
          layout="vertical"
          onFinish={handleBedSpaceSubmit}
        >
          <Form.Item
            label="Bed Label"
            name="bed_label"
            rules={[{ required: true, message: "Please enter bed label" }]}
          >
            <Input placeholder="e.g., 1A, 2B, etc." />
          </Form.Item>

          <Form.Item
            label="Rental Amount (ZMW)"
            name="rental_amount"
            rules={[{ required: true, message: "Please enter rental amount" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Enter monthly rental amount"
              min={0}
              formatter={(value) =>
                `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Rental Due Date"
                name="rental_due_date"
                rules={[{ required: true, message: "Please select due date" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Select due date"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Next Payment Date"
                name="next_payment_date"
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Select next payment date"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Overdue Amount (ZMW)"
                name="rent_overdue_amount"
                initialValue={0}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Enter overdue amount"
                  min={0}
                  formatter={(value) =>
                    `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select placeholder="Select bed space status">
                  <Select.Option value="Available">Available</Select.Option>
                  <Select.Option value="Occupied">Occupied</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingBedSpace ? "Update" : "Create"} Bed Space
              </Button>
              <Button onClick={() => setBedSpaceModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Rental Payment Modal */}
      <Modal
        title={
          <Space>
            <DollarCircleOutlined style={{ color: ZAMBIA_THEME.primary }} />
            Collect Rental Payment
            {selectedBedSpace && (
              <Tag color="blue">
                {selectedBedSpace.bed_label} - ZMW {selectedBedSpace.rental_amount}
              </Tag>
            )}
          </Space>
        }
        open={rentalPaymentModalVisible}
        onCancel={() => setRentalPaymentModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedBedSpace && (
          <div>
            {selectedBedSpace.rent_overdue_amount > 0 && (
              <div style={{ 
                background: "#fff2f0", 
                border: "1px solid #ffccc7", 
                borderRadius: 8, 
                padding: 16, 
                marginBottom: 16 
              }}>
                <Text type="danger" strong>
                  ⚠️ Overdue Amount: ZMW {selectedBedSpace.rent_overdue_amount}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Payments will first be applied to overdue amounts
                </Text>
              </div>
            )}
            
            <Form form={rentalPaymentForm} layout="vertical" onFinish={handleRentalPaymentSubmit}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label="Amount Paid (ZMW)"
                    name="amount_paid"
                    rules={[{ required: true, message: "Please enter amount paid" }]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Enter payment amount"
                      min={0}
                      formatter={value => `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/ZMW\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Transaction Date"
                    name="transaction_date"
                    rules={[{ required: true, message: "Please select transaction date" }]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Payment Method"
                    name="payment_method"
                    rules={[{ required: true, message: "Please select payment method" }]}
                  >
                    <Select placeholder="Select payment method">
                      <Select.Option value="cash">Cash</Select.Option>
                      <Select.Option value="bank_transfer">Bank Transfer</Select.Option>
                      <Select.Option value="mobile_money">Mobile Money</Select.Option>
                      <Select.Option value="card">Card</Select.Option>
                      <Select.Option value="cheque">Cheque</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Account"
                name="account_id"
                rules={[{ required: true, message: "Please select an account" }]}
              >
                <Select placeholder="Select account to credit">
                  {accounts.map(account => (
                    <Select.Option key={account.id} value={account.id}>
                      {account.account_name} (Balance: ZMW {account.balance?.toLocaleString() || '0'})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="New Overdue Amount (ZMW)"
                    name="rent_overdue_amount"
                    tooltip="Manually set the new overdue amount after this payment"
                    initialValue={selectedBedSpace?.rent_overdue_amount || 0}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Set overdue amount"
                      min={0}
                      formatter={value => `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/ZMW\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Next Payment Date"
                    name="next_payment_date"
                    tooltip="Set the next payment due date"
                    initialValue={selectedBedSpace?.next_payment_date ? moment(selectedBedSpace.next_payment_date) : null}
                  >
                    <DatePicker style={{ width: "100%" }} placeholder="Select next due date" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Reference"
                name="reference"
              >
                <Input placeholder="Payment reference (optional)" />
              </Form.Item>

              <Form.Item
                label="Notes"
                name="notes"
              >
                <Input.TextArea rows={3} placeholder="Additional notes (optional)" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    Record Payment
                  </Button>
                  <Button onClick={() => setRentalPaymentModalVisible(false)}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BoardingHouses;
